import {GeoRefApiInterface} from "../api/interface/georef";
import {PersistenceGeorefRefreshApiInterface} from "../api/interface/persistence-georef-refresh";
import {generateDefaultQueryError} from "../util/errorUtil";
import {type PersistenceAddressData, setLoading, setQueryError} from "./addressSlice";
import {removeDistinctAddress} from "./addressThunk";
import {selectCredentials} from "./authSlice";
import {selectGeorefRefreshLocked, selectRevaId, setPersistenceLoadingFinished} from "./persistenceSlice";
import type {RevaThunkAction} from "./revaHooks";

/**
 * Async thunk to request a georef refresh for an existing valuation. The refreshed valuation data is not
 * applied to the store: the caller reloads the page once the refresh succeeded.
 */
const refreshGeorefByRevaId =
    (revaId: string): RevaThunkAction<Promise<void>> =>
    async (dispatch, getState, {serviceApi}) => {
        const credentials = selectCredentials(getState());
        try {
            await new PersistenceGeorefRefreshApiInterface(revaId).execute(serviceApi, credentials!);
            dispatch(setPersistenceLoadingFinished());
        } catch (err) {
            dispatch(removeDistinctAddress());
            dispatch(setPersistenceLoadingFinished());
            throw err;
        }
    };

/**
 * Async thunk to request a georef refresh for an existing valuation. Returns true if the georef changed,
 * false if it did not (also false without any request once the valuation is at least pre-finalized or a
 * snapshot is loaded) and null if the request failed.
 */
export const uncacheAddressAsync =
    (addressData: PersistenceAddressData): RevaThunkAction<Promise<boolean | null>> =>
    async (dispatch, getState, {env, serviceApi}) => {
        // Begründung siehe selectGeorefRefreshLocked
        if (selectGeorefRefreshLocked(getState())) return false;
        const revaId = selectRevaId(getState());
        // Ohne geladene Bewertung ließe sich die neue Georeferenz nicht speichern - dann den externen
        // Cache (cache=false) gar nicht erst leeren. PageAddress bietet den Knopf nur mit gültiger RevaId an.
        if (!revaId) return null;
        const geoRefInterface = new GeoRefApiInterface(addressData.addressQuery, null, false);
        if (!geoRefInterface.validateAndDispatchQueryError((queryError) => dispatch(setQueryError(queryError)))) {
            return null;
        }
        dispatch(setLoading());
        const credentials = selectCredentials(getState());
        try {
            if (env.isDevelopmentMode) {
                console.log(`GeoRef query for address: ${addressData.addressQuery}`);
            }
            const result = await geoRefInterface.execute(serviceApi, credentials!);
            if (result.ambiguous) return false;
            const {addressMeta} = result;

            if (!isMoreThanOneMeter(addressMeta.lat, addressMeta.lon, addressData.lat, addressData.lon)) {
                if (env.isDevelopmentMode) {
                    console.info(`Georefs are too close`);
                    console.info(`Previously: ${addressData.addressQuery} (${addressData.lat}/ ${addressData.lon})`);
                    console.info(`Now: ${addressData.addressQuery} (${addressMeta.lat}/ ${addressMeta.lon})`);
                }
                return false;
            }

            if (env.isDevelopmentMode) {
                console.info(`Georef changed`);
                console.info(`Previously: ${addressData.addressQuery} (${addressData.lat}/ ${addressData.lon})`);
                console.info(`Now: ${addressData.addressQuery} (${addressMeta.lat}/ ${addressMeta.lon})`);
            }
            await dispatch(refreshGeorefByRevaId(revaId));
            return true;
        } catch (err) {
            dispatch(setQueryError(generateDefaultQueryError(err as Error)));
            return null;
        }
    };

function isMoreThanOneMeter(lat1: number, lon1: number, lat2: number, lon2: number): boolean {
    const R = 111_000; // Meter pro Grad

    const dLat = (lat2 - lat1) * R;
    const dLon = (lon2 - lon1) * R * Math.cos((lat1 * Math.PI) / 180);

    const distance = Math.sqrt(dLat * dLat + dLon * dLon);
    return distance > 1.0;
}
